/**
 * API Client для безопасного взаимодействия с Firebase Functions
 * Все запросы к БД идут через backend
 */

import { getAuth } from './firebase';

export const API_BASE_URL = import.meta.env.DEV
  ? 'http://localhost:5002/astrobaby-ai/europe-west1'
  : 'https://europe-west1-astrobaby-ai.cloudfunctions.net';

interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

/**
 * Helper to get current user's auth token
 */
async function getAuthToken(): Promise<string | null> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) {
    return null;
  }
  return await user.getIdToken();
}

/**
 * Helper to make authenticated API requests
 */
async function apiRequest<T = any>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  try {
    const token = await getAuthToken();
    if (!token) {
      return {
        success: false,
        error: 'User not authenticated'
      };
    }

    const response = await fetch(`${API_BASE_URL}/${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
    });

    const data = await response.json();
    
    if (!response.ok) {
      console.error(`API request failed: ${endpoint}`, response.status, data.error, data);
      return {
        success: false,
        error: data.error || `HTTP error! status: ${response.status}`
      };
    }

    return data;
  } catch (error) {
    console.error(`API request failed: ${endpoint}`, error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

/**
 * API client для работы с People
 */
export const peopleApi = {
  /**
   * Get all people for current user
   */
  async getAll() {
    return apiRequest<any[]>('getPeople', {
      method: 'GET',
    });
  },

  /**
   * Get single person by ID
   */
  async getById(personId: string) {
    return apiRequest<any>(`getPerson?id=${personId}`, {
      method: 'GET',
    });
  },

  /**
   * Create a new person
   */
  async create(personData: any) {
    return apiRequest<any>('createPerson', {
      method: 'POST',
      body: JSON.stringify(personData),
    });
  },

  /**
   * Update a person
   */
  async update(personId: string, updates: any) {
    return apiRequest<any>(`updatePerson?id=${personId}`, {
      method: 'POST', // Firebase Functions v2 uses POST for everything
      body: JSON.stringify(updates),
    });
  },

  /**
   * Delete a person
   */
  async delete(personId: string) {
    return apiRequest<any>(`deletePerson?id=${personId}`, {
      method: 'POST',
    });
  },
};

/**
 * API client для работы с Calculations
 */
export const calculationsApi = {
  /**
   * Get all calculations for current user
   */
  async getAll() {
    return apiRequest<any[]>('getUserCalculations', {
      method: 'GET',
    });
  },

  /**
   * Get single calculation by ID
   */
  async getById(calculationId: string) {
    return apiRequest<any>(`getCalculation?calculationId=${calculationId}`, {
      method: 'GET',
    });
  },

  /**
   * Create a new calculation
   */
  async create(calculationData: any) {
    return apiRequest<{ calculationId: string }>('saveCalculation', {
      method: 'POST',
      body: JSON.stringify(calculationData),
    });
  },

  /**
   * Delete a calculation
   */
  async delete(calculationId: string) {
    return apiRequest<any>(`deleteCalculation?calculationId=${calculationId}`, {
      method: 'POST',
    });
  },
};

/**
 * API client for Life Timeline copy generation
 */
export const lifeTimelineApi = {
  async generateCopy(payload: any) {
    return apiRequest<any>('generateLifeTimelineCopyApi', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
};

/**
 * API client для Astro Career
 */
export const astroCareerApi = {
  async generateAnalysis(payload: {
    natal: any;
    career_scores?: Record<string, number>;
    notes?: string;
    preferences?: {
      language?: 'ru' | 'en';
      featureName?: string;
    };
  }) {
    return apiRequest<any>('generateAstroCareerApi', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
};

/**
 * API client для работы с Natal Charts
 */
export const natalChartsApi = {
  /**
   * Get all natal charts for current user
   */
  async getAll() {
    return apiRequest<any[]>('getUserNatalCharts', {
      method: 'GET',
    });
  },

  /**
   * Save a natal chart
   */
  async save(chartData: any) {
    return apiRequest<{ chartId: string }>('saveNatalChart', {
      method: 'POST',
      body: JSON.stringify({ chartData }),
    });
  },
};

/**
 * API client для работы с Subscriptions
 */
export const subscriptionsApi = {
  /**
   * Get current user's subscription
   */
  async getCurrent() {
    return apiRequest<any>('getUserSubscription', {
      method: 'GET',
    });
  },

  /**
   * Cancel subscription
   */
  async cancel(subscriptionId: string) {
    return apiRequest<any>('cancelSubscription', {
      method: 'POST',
      body: JSON.stringify({ subscriptionId }),
    });
  },
};

/**
 * API client для работы с User Access
 */
export const userAccessApi = {
  /**
   * Check if user has premium purchase
   */
  async hasPremiumPurchase(productId: string = 'premium-calculation') {
    return apiRequest<{ hasPurchase: boolean }>(`checkPremiumPurchase?productId=${productId}`, {
      method: 'GET',
    });
  },

  /**
   * Get user access summary
   */
  async getUserAccess() {
    return apiRequest<any>('getUserAccess', {
      method: 'GET',
    });
  },
};

/**
 * API client для работы с Compatibility
 */
export const compatibilityApi = {
  /**
   * Get compatibility analysis
   */
  async getCompatibilityAnalysis(data: {
    partner1Chart: any;
    partner2Chart: any;
    compatibilityType?: string;
    partner1Name?: string;
    partner2Name?: string;
    partner1Gender?: 'male' | 'female' | 'other';
    partner2Gender?: 'male' | 'female' | 'other';
  }) {
    return apiRequest<any>('getCompatibilityAnalysis', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};

/**
 * API client для работы с Elections (Календарь Силы)
 */
export const electionsApi = {
  /**
   * Calculate election windows for a goal
   */
  async getElections(data: {
    text: string;
    range_days?: number;
    tz?: string;
    natal_profile_id?: string;
    latitude?: number;
    longitude?: number;
    start_date?: string; // YYYY-MM-DD format
  }) {
    return apiRequest<{
      windows: Array<{
        start: string;
        end: string;
        score: number;
        level: 'A' | 'B' | 'C' | 'D';
        factors: string[];
        explain_short: string;
        explain_long: string;
      }>;
      stops: Array<{
        start: string;
        end: string;
        reason: string;
        explain_short: string;
        explain_long: string;
      }>;
    }>('elections', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};

/**
 * API client для работы с разделами натальной карты
 */
export const natalChartSectionsApi = {
  /**
   * Generate all sections of natal chart analysis in one request
   */
  async generateAllSections(data: {
    chartData: {
      positions: Record<string, {
        sign: string;
        degree: number;
        house: number;
        absoluteDegree?: number;
      }>;
      ascendant?: number;
      midheaven?: number;
      cusps?: number[];
      aspects?: Array<{
        planet1: string;
        planet2: string;
        aspect: 'conjunction' | 'sextile' | 'square' | 'trine' | 'opposition';
        orb: number;
      }>;
    };
    personId: string;
    personGender?: 'male' | 'female' | 'other';
    birthDate?: string; // YYYY-MM-DD для определения возраста
    isTrial?: boolean; // Если true, генерируем только personality (бесплатно)
  }) {
    return apiRequest<{
      sections: any; // NatalChartSections type
      fromCache?: boolean;
    }>('generateNatalChartSectionApi', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};

/**
 * API client для работы с транзитами 2026
 */
export const transits2026Api = {
  /**
   * Get personal transits forecast for 2026 (uses "Me" person automatically)
   */
  async getTransits2026(force?: boolean) {
    return apiRequest<{
      forecast: {
        mainTheme: string;
        opportunities: string;
        transformations: string;
        challenges: string;
        relationships: string;
        workFinance: string;
        health: string;
        calendar: Array<{
          month: number;
          type: 'growth' | 'tension' | 'opportunity' | 'rest';
          description: string;
        }>;
      };
      transits: {
        monthlyTransits: Array<{
          month: number;
          transits: Array<{
            transitPlanet: string;
            natalPoint: string;
            aspect: string;
            house: number;
            date: string;
          }>;
        }>;
        majorTransits: Array<{
          planet: string;
          type: 'jupiter' | 'saturn' | 'outer';
          dates: string[];
          houses: number[];
        }>;
        activatedHouses: Record<number, number>;
      };
      natalChart: {
        sun: any;
        moon: any;
        ascendant: number;
        midheaven: number;
      };
      fromCache?: boolean;
    }>('transits2026', {
      method: 'POST',
      body: JSON.stringify({ force: force || false }),
    });
  },
};

interface ExchangeYandexCodePayload {
  code: string;
  state: string;
  redirectUri: string;
}

interface ExchangeYandexCodeResponse {
  success: boolean;
  customToken?: string;
  profile?: {
    id: string;
    email?: string;
    displayName?: string;
    avatarUrl?: string;
  };
  error?: string;
}

export async function exchangeYandexCode(payload: ExchangeYandexCodePayload): Promise<ExchangeYandexCodeResponse> {
  try {
    const response = await fetch(`${API_BASE_URL}/handleYandexOAuth`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data?.error || `HTTP error! status: ${response.status}`,
      };
    }

    return data;
  } catch (error) {
    console.error('exchangeYandexCode failed', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

interface ExchangeVkCodePayload {
  code: string;
  state: string;
  redirectUri: string;
  deviceId: string;
  codeVerifier?: string;
}

interface ExchangeVkCodeResponse {
  success: boolean;
  customToken?: string;
  profile?: {
    id: string;
    email?: string;
    displayName?: string;
    avatarUrl?: string;
  };
  error?: string;
}

export async function exchangeVkCode(payload: ExchangeVkCodePayload): Promise<ExchangeVkCodeResponse> {
  try {
    const requestPayload: any = {
      code: payload.code,
      state: payload.state,
      redirectUri: payload.redirectUri,
      deviceId: payload.deviceId,
    };
    
    if (payload.codeVerifier) {
      requestPayload.codeVerifier = payload.codeVerifier;
    }
    
    const response = await fetch(`${API_BASE_URL}/handleVkOAuth`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestPayload),
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data?.error || `HTTP error! status: ${response.status}`,
      };
    }

    return data;
  } catch (error) {
    console.error('exchangeVkCode failed', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Helper to make public API requests (no auth required)
 */
async function apiRequestPublic<T = any>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  try {
    const response = await fetch(`${API_BASE_URL}/${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    const data = await response.json();
    
    if (!response.ok) {
      return {
        success: false,
        error: data.error || `HTTP error! status: ${response.status}`
      };
    }

    return data;
  } catch (error) {
    console.error(`API request failed: ${endpoint}`, error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

/**
 * API client для отслеживания пользователей
 */
export const userTrackingApi = {
  /**
   * Track user registration
   */
  async trackRegistration(userId: string, provider: string, creationTime?: string, visitNumber?: number) {
    return apiRequestPublic('trackUserRegistration', {
      method: 'POST',
      body: JSON.stringify({ userId, provider, creationTime, visitNumber }),
    });
  },

  /**
   * Track user login
   */
  async trackLogin(userId: string, provider: string, lastSignInTime?: string) {
    return apiRequestPublic('trackUserLogin', {
      method: 'POST',
      body: JSON.stringify({ userId, provider, lastSignInTime }),
    });
  },

  /**
   * Track authentication error
   */
  async trackAuthError(provider: string, errorMessage: string, errorCode?: string, userId?: string) {
    return apiRequestPublic('trackAuthError', {
      method: 'POST',
      body: JSON.stringify({ provider, errorMessage, errorCode, userId }),
    });
  },
};

/**
 * Get latest daily prediction post
 */
export const getLatestDailyPrediction = async (): Promise<ApiResponse<{ text: string; postDate: string; eddDate: string | null; status: string }>> => {
  return apiRequestPublic('getLatestDailyPrediction', {
    method: 'GET'
  });
};

export default {
  people: peopleApi,
  calculations: calculationsApi,
  subscriptions: subscriptionsApi,
  userAccess: userAccessApi,
  userTracking: userTrackingApi,
  compatibility: compatibilityApi,
  elections: electionsApi,
  natalChartSections: natalChartSectionsApi,
  astroCareer: astroCareerApi,
};
